import { cva } from "class-variance-authority"
import type { ComponentProps, ReactNode } from "react"
import { useId } from "react"
import { cn } from "./lib"
import { Switch } from "./switch"

const fieldVariants = cva(
	"flex min-w-0 items-center justify-between border border-mm-border bg-mm-background",
	{
		variants: {
			variant: {
				default: "min-h-[70px] gap-3.5 rounded-[10px] p-3",
				compact: "min-h-[52px] gap-2.5 rounded-[9px] px-[11px] py-2.5",
				small: "min-h-[58px] gap-3.5 rounded-[10px] p-3",
				plain: "min-h-0 gap-3.5 rounded-[10px] border-0 p-0",
			},
			controlPosition: { start: "justify-start", end: "" },
		},
		defaultVariants: { variant: "default", controlPosition: "end" },
	},
)

export type SwitchFieldProps = Omit<
	ComponentProps<typeof Switch>,
	"children" | "size"
> & {
	label: ReactNode
	description?: ReactNode
	variant?: "default" | "compact" | "small" | "plain"
	controlPosition?: "start" | "end"
	/** className styles the row; controlClassName styles the underlying switch. */
	controlClassName?: string
}

/** A labeled controlled switch shares native form behavior, descriptions and row layouts. */
export function SwitchField({
	label,
	description,
	id,
	variant = "default",
	controlPosition = "end",
	className,
	controlClassName,
	...controlProps
}: SwitchFieldProps) {
	const generatedId = useId()
	const controlId = id ?? generatedId
	const labelId = `${controlId}-label`
	const descriptionId = `${controlId}-description`
	const hasDescription = description !== undefined && description !== null
	const describedBy =
		[
			hasDescription ? descriptionId : undefined,
			controlProps["aria-describedby"],
		]
			.filter(Boolean)
			.join(" ") || undefined
	return (
		<div
			data-slot="switch-field"
			className={cn(fieldVariants({ variant, controlPosition }), className)}
		>
			{/* :disabled includes inherited fieldsets as well as the explicit disabled prop. */}
			<label
				className="min-w-0 cursor-pointer has-[+button:disabled]:cursor-default"
				htmlFor={controlId}
			>
				<strong
					id={labelId}
					className={cn(
						"block font-bold",
						variant === "compact" ? "text-[11px]" : "text-[12px]",
						"leading-[1.4]",
					)}
				>
					{label}
				</strong>
				{hasDescription && (
					<small
						id={descriptionId}
						className={cn(
							"block text-mm-muted-fg",
							variant === "compact"
								? "mt-0.5 text-[9px] leading-[1.35]"
								: "mt-[3px] text-[10px] leading-[1.4]",
						)}
					>
						{description}
					</small>
				)}
			</label>
			<Switch
				{...controlProps}
				id={controlId}
				aria-labelledby={
					controlProps["aria-labelledby"] ??
					(controlProps["aria-label"] ? undefined : labelId)
				}
				aria-describedby={describedBy}
				// Runtime offsets survive optimized CSS and use logical direction in RTL layouts.
				className={cn(
					"data-[size=default]:h-[22px] data-[size=default]:w-[38px] shadow-none data-[state=checked]:bg-mm-info data-[state=unchecked]:bg-[var(--switch-field-track-off,var(--mm-border-strong))] [&_[data-slot=switch-thumb]]:size-[18px] [&_[data-slot=switch-thumb]]:[--switch-field-thumb-offset:2px] [&_[data-slot=switch-thumb]]:[translate:var(--switch-field-thumb-offset)_0] [&_[data-slot=switch-thumb][data-state=checked]]:[--switch-field-thumb-offset:18px] rtl:[&_[data-slot=switch-thumb]]:[--switch-field-thumb-offset:-2px] rtl:[&_[data-slot=switch-thumb][data-state=checked]]:[--switch-field-thumb-offset:-18px]",
					controlPosition === "start" && "-order-1",
					controlClassName,
				)}
			/>
		</div>
	)
}
